import * as React from "react";
import { AlertTriangle, Calculator, Info } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { fetchFxRate } from "../../api";
import { type AllowancePreview, type ExpenseLineItem } from "../../types";

/** One bill's own currency/amount, next to what it converted to in the
 *  destination currency - shown inline on a category row so a converted
 *  figure never looks mysteriously recalculated (confirmed desired -
 *  "show the conversion inline"). `convertedAmount` is null when no rate
 *  was available for this bill's currency/date (left out of the category's
 *  total and never flagged, same as the server's own
 *  _bill_amount_in_request_currency). */
interface BillConversion {
  currency: string;
  amount: number;
  convertedAmount: number | null;
}

interface CategoryUsageRow {
  category: string;
  /** Sum of every bill's convertedAmount in this category (bills with no
   *  rate available are left out) - always in preview.currency now, never
   *  a per-bill-currency figure. */
  spent: number;
  limit: number | null;
  /** The bills that make up `spent` in a currency OTHER than
   *  preview.currency, each with its own amount and what it converted to -
   *  a same-currency bill needs no conversion line (rate is always 1). */
  conversions: BillConversion[];
  /** True when at least one bill in this category couldn't be converted
   *  (no FX rate available) - a heads-up that `spent`/the limit comparison
   *  is missing that bill's amount, not a policy violation. */
  hasUnconverted: boolean;
}

/** Converts every bill into preview.currency via Frankfurter (the bill's
 *  own transaction date, same convention the FX reconciliation ledger
 *  uses - CurrencyConversionCard.tsx/lib/fx.ts), then groups by CATEGORY
 *  ONLY and compares each category's converted total against the
 *  destination's own whole-trip ceiling (preview.limits - the same per-day
 *  rate x day-count scaling estimate_amount applies everywhere else this
 *  figure is shown). Mirrors requests_api.py's
 *  _category_limit/_recompute_exceeds_policy exactly, so this matches what
 *  the server will actually flag a bill for.
 *
 *  Confirmed desired behaviour (client requirement change): a bill is no
 *  longer checked against its OWN currency's separately configured limit -
 *  it's always converted into the destination's and checked against THAT
 *  one limit. A same-currency bill needs no conversion (rate 1, always
 *  available, never fetched). A bill whose currency has no FX rate
 *  available for its date is left out of the category's total entirely and
 *  never flagged (see BillConversion's own doc comment) - same fail-soft
 *  rule the server's own _bill_amount_in_request_currency follows for a
 *  Frankfurter outage/unsupported pair. */
async function categoryUsage(
  preview: AllowancePreview,
  expenses: ExpenseLineItem[]
): Promise<CategoryUsageRow[]> {
  if (preview.claimMethod !== "reimbursement") return [];
  const conversions = await Promise.all(
    expenses.map(async (e): Promise<{ category: string } & BillConversion> => {
      const currency = e.currency || preview.currency;
      const amount = e.amount;
      if (currency === preview.currency) {
        return { category: e.category, currency, amount, convertedAmount: amount };
      }
      const date = e.date ? e.date.slice(0, 10) : undefined;
      const rate = await fetchFxRate(currency, preview.currency, date).catch(() => null);
      return {
        category: e.category,
        currency,
        amount,
        convertedAmount: rate === null ? null : amount * rate
      };
    })
  );
  const byCategory = new Map<string, BillConversion[]>();
  for (const { category, ...conv } of conversions) {
    const list = byCategory.get(category) ?? [];
    list.push(conv);
    byCategory.set(category, list);
  }
  const rows: CategoryUsageRow[] = [];
  for (const [category, list] of byCategory) {
    const spent = list.reduce((sum, c) => sum + (c.convertedAmount ?? 0), 0);
    if (spent <= 0 && !list.some((c) => c.convertedAmount === null)) continue;
    const perDay = preview.limits[category];
    const limit =
      perDay === undefined || perDay === null
        ? null
        : preview.fullDayCount * perDay + preview.halfDayCount * perDay * 0.5;
    rows.push({
      category,
      spent,
      limit,
      conversions: list.filter((c) => c.currency !== preview.currency),
      hasUnconverted: list.some((c) => c.convertedAmount === null)
    });
  }
  return rows;
}

/** A converted/summed amount, capped to 2 decimal places - an FX
 *  conversion otherwise prints every floating-point digit it happens to
 *  land on (e.g. "5,554.095", "377.386"), which no other money figure in
 *  this app shows. */
function fmtMoney(amount: number): string {
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** One category's per-day rate line, in one currency - "{currency} No
 *  limit" for a null rate, so a stacked multi-currency list (see
 *  PerDayLimitRows below) never has a bare "No limit" with nothing saying
 *  which currency it's for. */
function perDayLimitLine(currency: string, perDay: number | null): string {
  return perDay === null ? `${currency} No limit` : `${currency} ${fmtMoney(perDay)}`;
}

/** The static reference rows - category name, its configured per-day rate
 *  (or "No limit") in the trip's own/destination currency - shared by the
 *  always-visible pre-Bills-step panel and the Bills-step-onward info
 *  popover, so the two can never drift apart. Every bill is now converted
 *  into this one currency before being checked (see categoryUsage), so
 *  there is only ever one rate per category to show - no more a second,
 *  stacked line for whatever currency a bill happened to be added in. */
function PerDayLimitRows({
  preview
}: {
  preview: AllowancePreview & { claimMethod: "reimbursement" };
}) {
  return (
    <>
      {Object.keys(preview.limits).map((c) => (
        <div key={c} className="flex justify-between gap-2 text-muted-foreground">
          <span>{c}</span>
          <span className="text-right">{perDayLimitLine(preview.currency, preview.limits[c])}</span>
        </div>
      ))}
    </>
  );
}

// Live-updating sidebar: shows what the wizard's current fields resolve to
// under the active Policy Configuration. Not authoritative for what gets
// stored - the backend recomputes this same calculation again at submit
// time (see requests_api.py's create_my_request).
//
// The two claim methods show genuinely different things here, not the same
// box with extra bits added - Fixed Allowance has one real number (policy
// rate x day count, independent of whatever's on the optional Expenses
// step), so it keeps the "Estimated allowance" box exactly as before.
// Reimbursement has no equivalent single ceiling (every category has its
// own limit, or none) - "Estimated allowance" doesn't apply to it at all,
// so that box is replaced by a "Bills claimed" box: a plain table, one row
// per category with at least one bill, each showing its claimed total
// against that category's own whole-trip limit (or just the total, for a
// "No limit" category), with an over-limit row called out in red. `expenses`
// drives this breakdown - passed by both NewRequest.tsx's Bills step (as
// bills are added/edited/removed there) and ReviewRequestDialog.tsx (the
// request's already-saved bills, for an approver reviewing a Reimbursement
// claim's trip fields) - and is optional only because a brand-new request
// with no Draft yet genuinely has none to show.
export function AllowancePreviewCard({
  preview,
  loading,
  currency,
  expenses
}: {
  preview: AllowancePreview | null;
  loading: boolean;
  currency: string;
  expenses?: ExpenseLineItem[];
}) {
  // categoryUsage now does a live FX conversion per bill (Frankfurter), so
  // it's async - computed in an effect rather than inline on every render.
  // Re-fires whenever the bills themselves or the trip's own currency/day
  // counts change (anything that could change a conversion or a limit).
  const [usage, setUsage] = React.useState<CategoryUsageRow[]>([]);
  const [usageLoading, setUsageLoading] = React.useState(false);
  React.useEffect(() => {
    if (!preview || !expenses) {
      setUsage([]);
      return;
    }
    let cancelled = false;
    setUsageLoading(true);
    categoryUsage(preview, expenses)
      .then((rows) => !cancelled && setUsage(rows))
      .catch(() => !cancelled && setUsage([]))
      .finally(() => !cancelled && setUsageLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, expenses]);
  const showLiveUsage = !!expenses && usage.length > 0;
  // "By All Categories" is a check against a combined CEILING, so only
  // categories that actually have one (limit !== null) belong in it - a
  // "No limit" category has nothing to compare against and would otherwise
  // inflate the spent side with no matching limit (e.g. a "No limit"
  // Transportation bill of 200 on top of a Food 30-of-80 used to show
  // "230 of 80", which reads as 150 over when nothing is).
  const limitedUsage = usage.filter((row) => row.limit !== null);
  const totalSpent = limitedUsage.reduce((sum, row) => sum + row.spent, 0);
  const totalLimit = limitedUsage.reduce((sum, row) => sum + (row.limit ?? 0), 0);
  const totalOverLimit = totalSpent > totalLimit;
  // Every bill across every category, "No limit" ones included.
  const allSpent = usage.reduce((sum, row) => sum + row.spent, 0);
  // What the bills pay out: each limited category capped at its own limit,
  // "No limit" categories in full - the same "bills within policy" figure
  // Finance's Mark settled dialog prefills. Before any linked advance.
  const netPayable = usage.reduce(
    (sum, row) => sum + (row.limit === null ? row.spent : Math.min(row.spent, row.limit)),
    0
  );
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <Calculator className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">Allowance Preview</CardTitle>
        {/* The info button only appears once bills are actually in the
            picture (expenses given - the Bills step onward): that's when
            the static per-day list below gets replaced by the live
            category breakdown, so tucking the reference list behind a
            click keeps THIS screen from feeling crowded by two category
            lists at once. Before that (Travel Details and earlier), the
            static list is the only category content there is, so it's
            shown directly in the body instead - no click needed to see it,
            and no empty-feeling panel with nothing but Full/half days. */}
        {!loading && preview?.claimMethod === "reimbursement" && expenses && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="ml-auto text-muted-foreground hover:text-foreground"
                aria-label="View configured per-day policy limits"
              >
                <Info className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 space-y-1 p-3 text-xs" align="end">
              <p className="font-medium text-foreground">
                Per-category limits (per day)
              </p>
              <PerDayLimitRows preview={preview} />
            </PopoverContent>
          </Popover>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}

        {!loading && !preview && (
          <p className="text-sm text-muted-foreground">
            Fill in the travel details to see an estimated allowance.
          </p>
        )}

        {!loading && preview && (
          <>
            {preview.currency !== currency && (
              <p className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400">
                This policy is configured in {preview.currency}, not your
                request's {currency} - amounts below are shown in{" "}
                {preview.currency}.
              </p>
            )}
            <dl className="space-y-1.5 text-sm">
              <Row label="Full days" value={String(preview.fullDayCount)} />
              <Row label="Half days" value={String(preview.halfDayCount)} />
              {preview.claimMethod === "fixed_allowance" && (
                <Row
                  label="Per-day allowance"
                  value={`${preview.currency} ${fmtMoney(preview.perDayAmount)}`}
                />
              )}
            </dl>

            {/* Fixed Allowance: the policy-calculated headline box, exactly
                as before - one number from rate x day count, independent
                of the optional Expenses step, always shown.

                Reimbursement: `expenses` undefined means bills genuinely
                aren't relevant yet (NewRequest.tsx only passes them from
                the Bills step onward) - show NOTHING past Full/half days,
                same as this claim method always looked before bills had
                anywhere to total into (there's no single ceiling to show a
                headline number for). Once `expenses` IS given, the
                per-category table comes first, then its own total below -
                "here's each category, and here's what they add up to," not
                a number announced before the breakdown that explains it. */}
            {preview.claimMethod === "fixed_allowance" && (
              <>
                <div className="rounded-md bg-primary/10 p-3">
                  <p className="text-xs font-medium text-primary">
                    Estimated allowance
                  </p>
                  <p className="text-xl font-semibold text-primary">
                    {preview.currency}{" "}
                    {fmtMoney(preview.estimatedAllowanceAmount)}
                  </p>
                </div>
                <p className="text-[0.6875rem] text-muted-foreground">
                  This is an estimate based on the current policy. The final
                  amount will be confirmed during settlement.
                </p>
              </>
            )}

            {/* Before the Bills step (expenses undefined): the plain,
                always-visible per-day reference list - the original
                design, shown directly rather than behind the info button
                above (that button doesn't even render in this state). */}
            {preview.claimMethod === "reimbursement" && !expenses && (
              <div className="space-y-1 rounded-md border p-3 text-xs">
                <p className="font-medium text-foreground">
                  Per-category limits (per day)
                </p>
                <PerDayLimitRows preview={preview} />
              </div>
            )}

            {preview.claimMethod === "reimbursement" && expenses && (
              <>
                {showLiveUsage ? (
                  <div className="space-y-1 rounded-md border p-3 text-xs">
                    <p className="font-medium text-foreground">By category</p>
                    <table className="w-full">
                      <tbody>
                        {usage.map(({ category, spent, limit, conversions, hasUnconverted }) => {
                          const over = limit !== null && spent > limit;
                          return (
                            <tr key={category} className="align-top">
                              <td className="py-1 pr-2 text-muted-foreground">
                                {category}
                              </td>
                              <td
                                className={cn(
                                  "py-1 text-right tabular-nums",
                                  hasUnconverted
                                    ? "font-medium text-amber-600 dark:text-amber-400"
                                    : over
                                      ? "font-medium text-destructive"
                                      : "font-medium text-primary"
                                )}
                              >
                                {preview.currency} {fmtMoney(spent)}
                                {limit !== null &&
                                  ` of ${fmtMoney(limit)}`}
                                {/* Every bill in another currency converted
                                    into preview.currency - shown inline so
                                    the figure above never looks mysteriously
                                    recalculated (confirmed desired - "show
                                    the conversion inline"). A tap-to-open
                                    Popover, not hover-only text - hover does
                                    nothing on a touchscreen (same reasoning
                                    as the exceeds-policy icon in
                                    DetailCard.tsx's BillsSummaryCard). */}
                                {conversions.length > 0 && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className="ml-1 inline-flex align-middle text-muted-foreground"
                                        aria-label="How this was converted"
                                      >
                                        <Info className="h-3 w-3" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto space-y-1 p-2 text-xs" side="top">
                                      {conversions.map((c, i) => (
                                        <p key={i}>
                                          {c.currency} {fmtMoney(c.amount)}
                                          {c.convertedAmount === null
                                            ? " - no rate available, not counted"
                                            : ` -> ${preview.currency} ${fmtMoney(c.convertedAmount)}`}
                                        </p>
                                      ))}
                                    </PopoverContent>
                                  </Popover>
                                )}
                                {/* Not an over-limit flag - at least one
                                    bill in this category couldn't be
                                    converted (no FX rate available), so
                                    this total is incomplete, not a warning
                                    the server will also raise. */}
                                {hasUnconverted && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className="ml-1 inline-flex align-middle text-amber-600 dark:text-amber-400"
                                        aria-label="A bill in this category couldn't be converted"
                                      >
                                        <AlertTriangle className="h-3 w-3" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-2 text-xs" side="top">
                                      A bill in this category couldn't be converted (no exchange rate available) and isn't counted above.
                                    </PopoverContent>
                                  </Popover>
                                )}
                                {over && (
                                  <div className="font-normal text-destructive">
                                    Exceeds by {preview.currency}{" "}
                                    {fmtMoney(spent - limit)}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : usageLoading ? (
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Converting bills…
                  </p>
                ) : (
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Add a bill to see its category's total here.
                  </p>
                )}

                {limitedUsage.length > 0 && (
                  <div className={cn("rounded-md p-3 text-xs", totalOverLimit ? "bg-destructive/10" : "bg-primary/10")}>
                    <p className="font-medium text-foreground">
                      By All Categories
                    </p>
                    <p className={cn("font-medium", totalOverLimit ? "text-destructive" : "text-primary")}>
                      {preview.currency} {fmtMoney(totalSpent)} of {fmtMoney(totalLimit)}
                    </p>
                  </div>
                )}

                {allSpent > 0 && (
                  <div className="rounded-md bg-muted p-3">
                    <p className="text-xs font-medium text-muted-foreground">
                      Net payable
                    </p>
                    <p className="text-xl font-semibold text-foreground">
                      {preview.currency} {fmtMoney(netPayable)}
                    </p>
                    {netPayable < allSpent && (
                      <p className="mt-1 text-[0.6875rem] text-muted-foreground">
                        {preview.currency} {fmtMoney(allSpent)} claimed - {preview.currency}{" "}
                        {fmtMoney(allSpent - netPayable)} over the policy limit isn't payable.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
